import { Icona } from '../../components/Icona';
import { useEffect, useState } from 'react';
import { richieste } from '../../api/acquisti';
import type { DettaglioRichiesta, RigaRichiesta } from '../../api/acquisti';
import { formattaData, formattaImporto, formattaNumero } from '../../lib/formato';
import { ChiediMotivo } from './ChiediMotivo';
import { usePuo } from '../sessione/ContestoSessione';
import { DalPreventivo } from './DalPreventivo';
import { numeroDecimale } from '../../lib/numeri';

interface Props {
  richiestaId: string;
  onChiudi: () => void;
  onCambiata: () => void;
}

export function SchedaRichiesta({ richiestaId, onChiudi, onCambiata }: Props) {
  const puoFare = usePuo();
  const [richiesta, setRichiesta] = useState<DettaglioRichiesta | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  // Quale spiegazione si sta chiedendo: l'annullamento della richiesta, oppure la
  // chiusura del residuo di una riga. Null quando non si sta chiedendo niente.
  // Il pannello che pesca dal preventivo della commessa (16/10/2026).
  const [peschi, setPeschi] = useState(false);
  const [motivoPer, setMotivoPer] = useState<{ tipo: 'annulla' } | { tipo: 'riga'; riga: RigaRichiesta } | null>(null);

  useEffect(() => {
    let annullato = false;
    setRichiesta(null);
    setErrore(null);
    richieste
      .leggi(richiestaId)
      .then((r) => !annullato && setRichiesta(r))
      .catch((e: Error) => !annullato && setErrore(e.message));
    return () => {
      annullato = true;
    };
  }, [richiestaId]);

  useEffect(() => {
    const chiudiConEsc = (e: KeyboardEvent) => e.key === 'Escape' && onChiudi();
    window.addEventListener('keydown', chiudiConEsc);
    return () => window.removeEventListener('keydown', chiudiConEsc);
  }, [onChiudi]);

  /*  Ogni azione ricarica la richiesta dalla risposta del server invece di aggiornare
      quello che c'è a schermo: stato, evasione e residui li calcola il dominio, e
      ricalcolarli qui vorrebbe dire due implementazioni della stessa regola che prima o
      poi si smentiscono a vicenda.  */
  async function agisci(azione: () => Promise<DettaglioRichiesta>) {
    setInCorso(true);
    setErrore(null);
    try {
      setRichiesta(await azione());
      onCambiata();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  async function conMotivo(motivo: string) {
    if (!motivoPer) return;
    const azione = motivoPer;
    setMotivoPer(null);

    await agisci(() =>
      azione.tipo === 'annulla'
        ? richieste.annulla(richiestaId, motivo)
        : richieste.chiudiResiduoRiga(richiestaId, azione.riga.id, motivo),
    );
  }

  if (errore && !richiesta) {
    return (
      <aside className="scheda">
        <p className="errore">{errore}</p>
        <button type="button" onClick={onChiudi}>
          Chiudi
        </button>
      </aside>
    );
  }

  if (!richiesta) {
    return (
      <aside className="scheda">
        <p className="attesa">Carico la richiesta…</p>
      </aside>
    );
  }

  return (
    <aside className="scheda" aria-label={`Richiesta ${richiesta.numero ?? ''}`}>
      <header>
        <div>
          <p className="chiave">
            <strong>{richiesta.numero ?? 'senza numero'}</strong>
            <span className={`stato stato-${richiesta.stato.toLowerCase()}`}>{richiesta.stato}</span>
            {richiesta.stato !== 'Annullata' && puoFare('AnnullaRichieste') && (
              <span className={`stato evasione-${richiesta.evasione.toLowerCase()}`}>
                {richiesta.evasione === 'Nessuna' ? 'da ordinare' : `evasione ${richiesta.evasione}`}
              </span>
            )}
          </p>
          <h2>{richiesta.descrizione}</h2>
          <p className="riferimenti">
            {richiesta.commessa && <>commessa {richiesta.commessa} · </>}
            chiesta il {formattaData(richiesta.dataRichiesta)} · serve il{' '}
            {formattaData(richiesta.dataFabbisogno)}
            {richiesta.richiedente && ` · ${richiesta.richiedente}`}
          </p>
        </div>
        <button type="button" className="chiudi" onClick={onChiudi} aria-label="Chiudi la scheda">
          <Icona nome="chiudi" />
        </button>
      </header>

      {richiesta.motivoAnnullamento && (
        <p className="avviso">Annullata: {richiesta.motivoAnnullamento}</p>
      )}

      {richiesta.note && <p className="note">{richiesta.note}</p>}

      <table className="righe-richiesta">
        <thead>
          <tr>
            <th>Cosa</th>
            <th className="destra">Chiesta</th>
            <th className="destra">Ordinata</th>
            <th className="destra">Residuo</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {richiesta.righe.map((riga) => (
            <tr key={riga.id} className={riga.residuo === 0 ? 'chiusa' : ''}>
              <td>
                <span className="descrizione">{riga.descrizione}</span>
                <span className="dettaglio">
                  {riga.siglaProduttore && (
                    <em className="chiave">
                      {riga.siglaProduttore} {riga.codiceArticoloProduttore}
                    </em>
                  )}
                  {riga.daCodificare && <em className="da-codificare">da codificare</em>}
                  {riga.fornitoreSuggerito && (
                    <span>
                      suggerito {riga.fornitoreSuggerito}
                      {riga.costoStimato !== null && <> a {formattaImporto(riga.costoStimato)}</>}
                    </span>
                  )}
                  {riga.motivoChiusura && <em className="motivo">chiusa: {riga.motivoChiusura}</em>}
                  {riga.eccedenza > 0 && (
                    <em className="motivo">
                      ordinati {formattaNumero(riga.eccedenza)} in più: confezione minima
                    </em>
                  )}
                </span>
              </td>
              <td className="destra">
                {formattaNumero(riga.quantita)} {riga.unitaMisura}
              </td>
              <td className="destra">{formattaNumero(riga.quantitaOrdinata)}</td>
              <td className="destra">{formattaNumero(riga.residuo)}</td>
              <td className="azioni-riga">
                {riga.residuo > 0 && richiesta.stato === 'Inviata' && (
                  <button
                    type="button"
                    disabled={inCorso}
                    title="Chiude quello che non verrà più comprato"
                    onClick={() => setMotivoPer({ tipo: 'riga', riga })}
                  >
                    chiudi
                  </button>
                )}
                {riga.residuo > 0 && richiesta.stato === 'Bozza' && (
                  <button
                    type="button"
                    disabled={inCorso}
                    onClick={() => void agisci(() => richieste.rimuoviRiga(richiestaId, riga.id))}
                  >
                    togli
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4}>Valore stimato</td>
            <td className="destra">{formattaImporto(richiesta.valoreStimato)}</td>
          </tr>
        </tfoot>
      </table>

      {/*  LA RICHIESTA SI COMPILA QUI, finche' e' in bozza: dal preventivo della commessa
           con il «tastino», o a mano per quello che il preventivo non diceva (Galizzi,
           16/10/2026: «lo selezioni, poi puoi aggiungere anche altre cose»).  */}
      {richiesta.stato === 'Bozza' && puoFare('CreaRichieste') && (
        <>
          {peschi ? (
            <DalPreventivo
              richiesta={richiesta}
              onAggiunte={(r) => {
                setRichiesta(r);
                onCambiata();
              }}
              onChiudi={() => setPeschi(false)}
            />
          ) : (
            <p className="comandi-richiesta">
              <button type="button" disabled={inCorso} onClick={() => setPeschi(true)}>
                Dal preventivo…
              </button>
            </p>
          )}
          <RigaAMano
            inCorso={inCorso}
            onAggiungi={(riga) => agisci(() => richieste.aggiungiRiga(richiestaId, riga))}
          />
        </>
      )}

      {errore && <p className="errore">{errore}</p>}

      {motivoPer && (
        <ChiediMotivo
          domanda={
            motivoPer.tipo === 'annulla'
              ? 'Perché si annulla questa richiesta?'
              : `Perché non si comprerà più «${motivoPer.riga.descrizione}»?`
          }
          etichettaConferma={motivoPer.tipo === 'annulla' ? 'Annulla la richiesta' : 'Chiudi il residuo'}
          inCorso={inCorso}
          onConferma={(motivo) => void conMotivo(motivo)}
          onAnnulla={() => setMotivoPer(null)}
        />
      )}

      <div className="azioni">
        {/*  I bottoni si nascondono, non si disabilitano: un bottone grigio invita a
             chiedere perché, e la risposta — «quel permesso non ce l'hai» — non è una
             cosa che si sistema insistendo. Chi chiede il materiale non lo compra, quindi
             per il tecnico questa colonna è più corta e non è un errore.  */}
        {richiesta.stato === 'Bozza' && puoFare('InviaRichieste') && (
          <button
            type="button"
            className="primario"
            disabled={inCorso || richiesta.righe.length === 0}
            onClick={() => void agisci(() => richieste.invia(richiestaId))}
          >
            Invia all’ufficio acquisti
          </button>
        )}

        {richiesta.stato === 'Inviata' && richiesta.evasione === 'Nessuna' && puoFare('InviaRichieste') && (
          <button
            type="button"
            disabled={inCorso}
            title="Si può solo se nessuna riga è stata ordinata"
            onClick={() => void agisci(() => richieste.riportaInBozza(richiestaId))}
          >
            Riporta in bozza
          </button>
        )}

        {richiesta.stato !== 'Annullata' && (
          <button
            type="button"
            className="pericolo"
            disabled={inCorso}
            onClick={() => setMotivoPer({ tipo: 'annulla' })}
          >
            Annulla la richiesta
          </button>
        )}
      </div>
    </aside>
  );
}

/*  UNA RIGA SCRITTA A MANO: quello che il preventivo non diceva. Senza articolo — la
    codifica la fa chi compra, e la riga resta «da codificare» finche' non succede.  */
function RigaAMano({
  inCorso,
  onAggiungi,
}: {
  inCorso: boolean;
  onAggiungi: (riga: { descrizione: string; quantita: number; unitaMisura: string | null }) => Promise<void>;
}) {
  const [descrizione, setDescrizione] = useState('');
  const [quantita, setQuantita] = useState('1');
  const [unita, setUnita] = useState('NR');

  const q = numeroDecimale(quantita) ?? 0;
  const pronta = descrizione.trim() !== '' && q > 0;

  async function aggiungi() {
    if (!pronta) return;
    await onAggiungi({
      descrizione: descrizione.trim(),
      quantita: q,
      unitaMisura: unita.trim() === '' ? null : unita.trim(),
    });
    setDescrizione('');
    setQuantita('1');
  }

  return (
    <p className="riga-a-mano">
      <input
        aria-label="Cosa serve, a mano"
        placeholder="+ riga a mano: cosa serve"
        value={descrizione}
        onChange={(e) => setDescrizione(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && void aggiungi()}
      />
      <input
        className="quantita"
        inputMode="decimal"
        aria-label="Quantità"
        value={quantita}
        onChange={(e) => setQuantita(e.target.value)}
      />
      <input
        className="unita"
        aria-label="Unità di misura"
        value={unita}
        onChange={(e) => setUnita(e.target.value)}
      />
      <button type="button" disabled={inCorso || !pronta} onClick={() => void aggiungi()}>
        Aggiungi
      </button>
    </p>
  );
}
